import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { format, startOfDay, endOfDay } from 'date-fns';
import { Habit, HabitStack, HabitCompletion, DailyProgress, StreakData } from '../types';
import { HabitMetricsService } from '../services/HabitMetricsService';

interface HabitsContextType {
  habits: Habit[];
  habitStacks: HabitStack[];
  habitCompletions: HabitCompletion[];
  todayProgress: DailyProgress | null;
  loading: boolean;
  
  // Habit management
  createHabit: (habitData: Omit<Habit, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateHabit: (habitId: string, updates: Partial<Habit>) => Promise<void>;
  deleteHabit: (habitId: string) => Promise<void>;
  
  // Habit Stack management
  createHabitStack: (stackData: Omit<HabitStack, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateHabitStack: (stackId: string, updates: Partial<HabitStack>) => Promise<void>;
  deleteHabitStack: (stackId: string) => Promise<void>;
  
  // Progress tracking
  completeHabit: (habitId: string, habitStackId?: string, notes?: string, customValue?: string | number) => Promise<void>;
  uncompleteHabit: (habitId: string) => Promise<void>;
  isHabitCompletedToday: (habitId: string) => boolean;
  isStackCompletedToday: (stackId: string) => boolean;
  getTodayCompletions: () => HabitCompletion[];
  getCurrentStreak: (habitId?: string) => number;
  getLongestStreak: (habitId?: string) => number;
  getBestCurrentStreak: () => number;
  getCompletionRate: (habitId?: string) => number;
  
  // Smart streak system
  getStreakData: () => StreakData | null;
  updateStreakData: () => Promise<void>;
  getTodayCompletionPercentage: () => number;
  recalculateStreaks: () => void;
  
  // History management
  getHabitHistory: (habitId: string, startDate: string, endDate: string) => Promise<Array<{
    date: string;
    completed: boolean;
    customValue?: string | number;
    notes?: string;
  }>>;
  addHistoryCompletion: (habitId: string, date: string, data: {
    customValue?: string | number;
    notes?: string;
  }) => Promise<void>;
  removeHistoryCompletion: (habitId: string, date: string) => Promise<void>;
  
  // Data refresh
  refreshData: () => Promise<void>;
  refreshHabitData: (habitId: string) => Promise<void>;
  
  // Habit Metrics System
  getHabitMetrics: (habitId: string) => Promise<any>;
  initializeHabitMetrics: (habitId: string) => Promise<void>;
}

const HabitsContext = createContext<HabitsContextType | undefined>(undefined);

export const useHabits = () => {
  const context = useContext(HabitsContext);
  if (context === undefined) {
    throw new Error('useHabits must be used within a HabitsProvider');
  }
  return context;
};

export const HabitsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [habits, setHabits] = useState<Habit[]>([]);
  const [habitStacks, setHabitStacks] = useState<HabitStack[]>([]);
  const [habitCompletions, setHabitCompletions] = useState<HabitCompletion[]>([]);
  const [todayProgress, setTodayProgress] = useState<DailyProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState<StreakData | null>(null);

  // Log do estado atual das completions para debug
  useEffect(() => {
    console.log('🔍 Estado atual do contexto:', {
      habitsCount: habits.length,
      stacksCount: habitStacks.length,
      completionsCount: habitCompletions.length,
      completions: habitCompletions.map(c => ({
        id: c.id,
        habitId: c.habitId,
        completedAt: c.completedAt.toISOString()
      })),
      loading
    });
  }, [habits.length, habitStacks.length, habitCompletions, loading]);

  // Funções de consulta local (sem buscar no Firestore)
  const getTodayCompletions = useCallback((): HabitCompletion[] => {
    const today = new Date();
    const startOfToday = startOfDay(today);
    const endOfToday = endOfDay(today);
    
    const completions = habitCompletions.filter(completion => {
      const completionDate = completion.completedAt;
      return completionDate >= startOfToday && completionDate <= endOfToday;
    });
    
    console.log('🔍 getTodayCompletions:', {
      today: today.toISOString(),
      startOfToday: startOfToday.toISOString(),
      endOfToday: endOfToday.toISOString(),
      totalCompletions: habitCompletions.length,
      todayCompletions: completions.length,
      completions: completions.map(c => ({
        id: c.id,
        habitId: c.habitId,
        completedAt: c.completedAt.toISOString()
      }))
    });
    
    return completions;
  }, [habitCompletions]);

  // SEQUÊNCIA ATUAL GERAL - Para dashboard (dias consecutivos com pelo menos 1 conclusão)
  const getBestCurrentStreak = useCallback((): number => {
    if (!currentUser) return 0;

    try {
      console.log('🔥 Calculando sequência atual geral...');
      
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let currentStreak = 0;
      let checkDate = new Date(yesterday);
      
      // Conta sequência consecutiva até ontem
      while (true) {
        const dateStr = format(checkDate, 'yyyy-MM-dd');
        
        // Verifica se houve QUALQUER conclusão nesta data (qualquer hábito)
        const hasAnyCompletion = habitCompletions.some(completion => {
          const completionDate = format(completion.completedAt, 'yyyy-MM-dd');
          return completionDate === dateStr;
        });
        
        if (hasAnyCompletion) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
        
        // Proteção contra loop infinito (máximo 365 dias)
        if (currentStreak > 365) break;
      }
      
      // Se houve qualquer conclusão hoje, adiciona +1
      const todayCompletions = getTodayCompletions();
      if (todayCompletions.length > 0) {
        currentStreak++;
      }
      
      console.log('🔥 Sequência atual geral calculada:', currentStreak);
      return currentStreak;
      
    } catch (error) {
      console.error('Erro ao calcular sequência atual geral:', error);
      return 0;
    }
  }, [currentUser, habitCompletions, getTodayCompletions]);

  // SEQUÊNCIA ATUAL - Para detalhes do hábito (conta até ontem + hoje se completado)
  const getCurrentStreak = useCallback((habitId?: string): number => {
    if (!currentUser) return 0;

    try {
      console.log('🔥 Calculando sequência atual...', { habitId });
      
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let currentStreak = 0;
      let checkDate = new Date(yesterday);
      
      // Procura sequência consecutiva contando até ontem
      while (true) {
        const dateStr = format(checkDate, 'yyyy-MM-dd');
        
        // Verifica se completou nesta data
        const completed = habitCompletions.some(completion => {
          const completionDate = format(completion.completedAt, 'yyyy-MM-dd');
          const matchesDate = completionDate === dateStr;
          const matchesHabit = habitId ? completion.habitId === habitId : true;
          
          return matchesDate && matchesHabit;
        });
        
        if (completed) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
        
        // Proteção contra loop infinito (máximo 365 dias)
        if (currentStreak > 365) break;
      }
      
      // Se completou este hábito hoje, adiciona +1
      const todayCompletions = getTodayCompletions();
      if (habitId) {
        const habitCompletedToday = todayCompletions.some(completion => completion.habitId === habitId);
        if (habitCompletedToday) {
          currentStreak++;
        }
      } else if (todayCompletions.length > 0) {
        // Se não especificou hábito e houve qualquer conclusão hoje
        currentStreak++;
      }
      
      console.log('🔥 Sequência atual calculada:', currentStreak);
      return currentStreak;
      
    } catch (error) {
      console.error('Erro ao calcular sequência atual:', error);
      return 0;
    }
  }, [habitCompletions, currentUser, getTodayCompletions]);

  // MAIOR SEQUÊNCIA - Para dashboard (all-time record)
  const getLongestStreak = useCallback((habitId?: string): number => {
    if (!currentUser) return 0;

    try {
      console.log('🏆 Calculando maior sequência...', { habitId });
      
      // Filtra completions do hábito específico ou todos
      const relevantCompletions = habitCompletions.filter(completion => {
        return habitId ? completion.habitId === habitId : true;
      });
      
      if (relevantCompletions.length === 0) return 0;
      
      // Ordena por data
      const sortedCompletions = relevantCompletions.sort((a, b) => 
        a.completedAt.getTime() - b.completedAt.getTime()
      );
      
      // Agrupa por data (pode ter múltiplas conclusões no mesmo dia)
      const completionsByDate = new Set<string>();
      sortedCompletions.forEach(completion => {
        const dateStr = format(completion.completedAt, 'yyyy-MM-dd');
        completionsByDate.add(dateStr);
      });
      
      const uniqueDates = Array.from(completionsByDate).sort();
      
      let longestStreak = 0;
      let currentStreak = 0;
      let previousDate: Date | null = null;
      
      uniqueDates.forEach(dateStr => {
        const currentDate = new Date(dateStr);
        
        if (previousDate === null) {
          // Primeiro dia
          currentStreak = 1;
        } else {
          // Verifica se é dia consecutivo
          const expectedDate = new Date(previousDate);
          expectedDate.setDate(expectedDate.getDate() + 1);
          
          if (format(expectedDate, 'yyyy-MM-dd') === dateStr) {
            // Dia consecutivo
            currentStreak++;
          } else {
            // Quebrou a sequência
            longestStreak = Math.max(longestStreak, currentStreak);
            currentStreak = 1;
          }
        }
        
        previousDate = currentDate;
      });
      
      // Verifica a última sequência
      longestStreak = Math.max(longestStreak, currentStreak);
      
      // Se completou hoje, a sequência atual pode ser maior que a registrada
      const today = format(new Date(), 'yyyy-MM-dd');
      const completedToday = habitCompletions.some(completion => {
        const completionDate = format(completion.completedAt, 'yyyy-MM-dd');
        const matchesDate = completionDate === today;
        const matchesHabit = habitId ? completion.habitId === habitId : true;
        
        return matchesDate && matchesHabit;
      });
      
      if (completedToday) {
        const currentIncludingToday = getCurrentStreak(habitId) + 1;
        longestStreak = Math.max(longestStreak, currentIncludingToday);
      }
      
      console.log('🏆 Maior sequência calculada:', longestStreak);
      return longestStreak;
      
    } catch (error) {
      console.error('Erro ao calcular maior sequência:', error);
      return 0;
    }
  }, [habitCompletions, currentUser, getCurrentStreak]);

  // Função auxiliar para atualizar progresso diário
  const updateDailyProgress = useCallback(async () => {
    if (!currentUser) return;

    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayCompletions = getTodayCompletions();
      
      const completedHabits = todayCompletions
        .filter(c => !c.habitStackId)
        .map(c => c.habitId);
      const completedStacks = todayCompletions
        .filter(c => c.habitStackId)
        .map(c => c.habitStackId!);

      const totalHabits = habits.length + habitStacks.length;
      const completionPercentage = totalHabits > 0 ? 
        ((completedHabits.length + completedStacks.length) / totalHabits) * 100 : 0;

      const progressData = {
        userId: currentUser.uid,
        date: today,
        completedHabits,
        completedStacks,
        totalHabits,
        completionPercentage,
        streak: getBestCurrentStreak(), // Usa a sequência atual geral para o dashboard
        createdAt: Timestamp.now()
      };

      // Verificar se já existe progresso para hoje
      const progressQuery = query(
        collection(db, 'dailyProgress'),
        where('userId', '==', currentUser.uid),
        where('date', '==', today)
      );

      const progressSnapshot = await getDocs(progressQuery);
      if (progressSnapshot.empty) {
        await addDoc(collection(db, 'dailyProgress'), progressData);
      } else {
        const progressRef = doc(db, 'dailyProgress', progressSnapshot.docs[0].id);
        await updateDoc(progressRef, progressData);
      }

      // Atualizar estado local
      setTodayProgress({
        ...progressData,
        createdAt: new Date()
      } as DailyProgress);

    } catch (error: any) {
      console.error('Erro ao atualizar progresso diário:', error);
    }
  }, [currentUser, habits.length, habitStacks.length, getTodayCompletions, getBestCurrentStreak]);

  // OTIMIZAÇÃO: Carregar todos os dados apenas uma vez ao abrir a página
  const loadAllData = useCallback(async () => {
    if (!currentUser) {
      setHabits([]);
      setHabitStacks([]);
      setHabitCompletions([]);
      setTodayProgress(null);
      setStreakData(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // 1. Carregar hábitos
      const habitsQuery = query(
        collection(db, 'habits'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const habitsSnapshot = await getDocs(habitsQuery);
      const habitsData = habitsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Habit[];

      // 2. Carregar stacks
      const stacksQuery = query(
        collection(db, 'habitStacks'),
        where('userId', '==', currentUser.uid),
        orderBy('order', 'asc')
      );
      const stacksSnapshot = await getDocs(stacksQuery);
      const stacksData = stacksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as HabitStack[];

      // 3. Carregar completions dos últimos 30 dias apenas
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const completionsQuery = query(
        collection(db, 'habitCompletions'),
        where('userId', '==', currentUser.uid),
        where('completedAt', '>=', Timestamp.fromDate(thirtyDaysAgo))
      );
      const completionsSnapshot = await getDocs(completionsQuery);
      const completionsData = completionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        completedAt: doc.data().completedAt?.toDate()
      })) as HabitCompletion[];

      // 4. Carregar progresso de hoje
      const today = format(new Date(), 'yyyy-MM-dd');
      const progressQuery = query(
        collection(db, 'dailyProgress'),
        where('userId', '==', currentUser.uid),
        where('date', '==', today)
      );
      const progressSnapshot = await getDocs(progressQuery);
      let progressData = null;
      if (!progressSnapshot.empty) {
        progressData = {
          ...progressSnapshot.docs[0].data(),
          createdAt: progressSnapshot.docs[0].data().createdAt?.toDate()
        } as DailyProgress;
      }

      // 5. Carregar dados de streak
      const streakDocRef = doc(db, 'streakData', currentUser.uid);
      const streakDoc = await getDoc(streakDocRef);
      let streakDataResult = null;
      if (streakDoc.exists()) {
        streakDataResult = streakDoc.data() as StreakData;
      }

      // Atualizar todos os estados de uma vez
      setHabits(habitsData);
      setHabitStacks(stacksData);
      setHabitCompletions(completionsData);
      setTodayProgress(progressData);
      setStreakData(streakDataResult);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // OTIMIZAÇÃO: Recarregar dados de um hábito específico após mudanças
  const refreshHabitData = useCallback(async (habitId: string) => {
    if (!currentUser) return;

    try {
      // Buscar apenas completions atualizados deste hábito nos últimos 30 dias
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const completionsQuery = query(
        collection(db, 'habitCompletions'),
        where('userId', '==', currentUser.uid),
        where('habitId', '==', habitId),
        where('completedAt', '>=', Timestamp.fromDate(thirtyDaysAgo))
      );
      
      const completionsSnapshot = await getDocs(completionsQuery);
      const newCompletions = completionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        completedAt: doc.data().completedAt?.toDate()
      })) as HabitCompletion[];

      // Atualizar apenas as completions deste hábito específico
      setHabitCompletions(prevCompletions => {
        const filtered = prevCompletions.filter(comp => comp.habitId !== habitId);
        return [...filtered, ...newCompletions];
      });

      // Atualizar progresso diário
      await updateDailyProgress();

    } catch (error) {
      console.error('Erro ao atualizar dados do hábito:', error);
    }
  }, [currentUser, updateDailyProgress]);

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const createHabit = async (habitData: Omit<Habit, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      const newHabit = {
        ...habitData,
        userId: currentUser.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'habits'), newHabit);
      
      // Adicionar o novo hábito ao estado local
      const createdHabit = {
        id: docRef.id,
        ...habitData,
        userId: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      } as Habit;
      
      setHabits(prev => [createdHabit, ...prev]);
      
      // Inicializar métricas para hábitos com valores personalizados numéricos
      if (habitData.hasCustomValue && habitData.customValueType === 'number') {
        console.log('🔢 Inicializando métricas para hábito numérico:', docRef.id);
        await initializeHabitMetrics(docRef.id);
      }
      
    } catch (error: any) {
      console.error('Erro ao criar hábito:', error);
      throw error;
    }
  };

  const updateHabit = async (habitId: string, updates: Partial<Habit>) => {
    try {
      const habitRef = doc(db, 'habits', habitId);
      await updateDoc(habitRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      // Atualizar estado local
      setHabits(prev => prev.map(habit => 
        habit.id === habitId 
          ? { ...habit, ...updates, updatedAt: new Date() }
          : habit
      ));
      
    } catch (error) {
      console.error('Erro ao atualizar hábito:', error);
      throw error;
    }
  };

  const deleteHabit = async (habitId: string) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      // Deletar hábito
      await deleteDoc(doc(db, 'habits', habitId));
      
      // Remover do estado local
      setHabits(prev => prev.filter(habit => habit.id !== habitId));
      
      // Remover completions deste hábito do estado local
      setHabitCompletions(prev => prev.filter(comp => comp.habitId !== habitId));
      
      // Atualizar progresso diário
      await updateDailyProgress();
      
    } catch (error: any) {
      console.error('Erro ao deletar hábito:', error);
      throw error;
    }
  };

  const createHabitStack = async (stackData: Omit<HabitStack, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      const newStack = {
        ...stackData,
        userId: currentUser.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'habitStacks'), newStack);
      
      // Adicionar ao estado local
      const createdStack = {
        id: docRef.id,
        ...stackData,
        userId: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      } as HabitStack;
      
      setHabitStacks(prev => [...prev, createdStack]);
      
    } catch (error: any) {
      console.error('Erro ao criar stack:', error);
      throw error;
    }
  };

  const updateHabitStack = async (stackId: string, updates: Partial<HabitStack>) => {
    try {
      const stackRef = doc(db, 'habitStacks', stackId);
      await updateDoc(stackRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });

      // Atualizar estado local
      setHabitStacks(prev => prev.map(stack => 
        stack.id === stackId 
          ? { ...stack, ...updates, updatedAt: new Date() }
          : stack
      ));
      
    } catch (error) {
      console.error('Erro ao atualizar stack:', error);
      throw error;
    }
  };

  const deleteHabitStack = async (stackId: string) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      // Deletar stack
      await deleteDoc(doc(db, 'habitStacks', stackId));
      
      // Remover do estado local
      setHabitStacks(prev => prev.filter(stack => stack.id !== stackId));
      
      // Remover completions deste stack do estado local
      setHabitCompletions(prev => prev.filter(comp => comp.habitStackId !== stackId));
      
      // Atualizar progresso diário
      await updateDailyProgress();
      
    } catch (error: any) {
      console.error('Erro ao deletar stack:', error);
      throw error;
    }
  };

  const completeHabit = async (habitId: string, habitStackId?: string, notes?: string, customValue?: string | number) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      console.log('🟡 Iniciando completeHabit para:', habitId);
      
      // Verificar se já foi completado hoje usando dados locais atuais
      const today = new Date();
      const startOfToday = startOfDay(today);
      const endOfToday = endOfDay(today);
      
      const alreadyCompleted = habitCompletions.some(completion => {
        if (completion.habitId !== habitId) return false;
        const completionDate = completion.completedAt;
        return completionDate >= startOfToday && completionDate <= endOfToday;
      });

      if (alreadyCompleted) {
        console.log('⚠️ Hábito já completado hoje:', habitId);
        return; // Já foi completado hoje
      }

      // Criar completion
      const completionData: any = {
        userId: currentUser.uid,
        habitId,
        completedAt: Timestamp.now()
      };

      // Adicionar campos opcionais apenas se não forem undefined
      if (habitStackId !== undefined) {
        completionData.habitStackId = habitStackId;
      }
      if (notes !== undefined) {
        completionData.notes = notes;
      }
      if (customValue !== undefined) {
        completionData.customValue = customValue;
      }

      console.log('🟡 Salvando no Firestore...', completionData);
      const docRef = await addDoc(collection(db, 'habitCompletions'), completionData);
      console.log('✅ Salvo no Firestore com ID:', docRef.id);
      
      // Adicionar ao estado local APÓS confirmação do Firestore
      const newCompletion = {
        id: docRef.id,
        ...completionData,
        completedAt: new Date()
      } as HabitCompletion;
      
      setHabitCompletions(prev => {
        console.log('🟡 Atualizando estado local:', [...prev, newCompletion]);
        return [...prev, newCompletion];
      });

      // Atualizar métricas se o hábito tem valor customizado numérico
      if (customValue !== undefined && typeof customValue === 'number') {
        try {
          await HabitMetricsService.addCompletion(currentUser.uid, habitId, customValue);
          console.log('📊 Métricas atualizadas para hábito:', habitId);
        } catch (metricsError) {
          console.warn('⚠️ Erro ao atualizar métricas (não crítico):', metricsError);
          // Não falha a completion se houver erro nas métricas
        }
      }
      
      // Atualizar progresso diário (sem refreshHabitData para evitar conflitos)
      await updateDailyProgress();
      
      console.log('✅ completeHabit finalizado com sucesso');
      
    } catch (error: any) {
      console.error('❌ Erro ao completar hábito:', error);
      throw error;
    }
  };

  const uncompleteHabit = async (habitId: string) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      console.log('🟡 Iniciando uncompleteHabit para:', habitId);
      
      const today = new Date();
      const startOfToday = startOfDay(today);
      const endOfToday = endOfDay(today);

      // Encontrar completions de hoje para este hábito
      const todayCompletions = habitCompletions.filter(completion => {
        if (completion.habitId !== habitId) return false;
        const completionDate = completion.completedAt;
        return completionDate >= startOfToday && completionDate <= endOfToday;
      });

      if (todayCompletions.length === 0) {
        console.log('⚠️ Nenhuma completion de hoje encontrada para:', habitId);
        return; // Nenhuma completion de hoje para deletar
      }

      console.log('🟡 Deletando completions de hoje do Firestore:', todayCompletions.map(c => c.id));

      // Deletar do Firestore
      const deletePromises = todayCompletions.map(completion => 
        deleteDoc(doc(db, 'habitCompletions', completion.id))
      );
      await Promise.all(deletePromises);
      
      console.log('✅ Deletado do Firestore');

      // Atualizar métricas para completions que tinham valores numéricos
      for (const completion of todayCompletions) {
        if (completion.customValue !== undefined && typeof completion.customValue === 'number') {
          try {
            await HabitMetricsService.removeCompletion(currentUser.uid, habitId, completion.customValue);
            console.log('📊 Métricas atualizadas (remoção) para hábito:', habitId);
          } catch (metricsError) {
            console.warn('⚠️ Erro ao atualizar métricas na remoção (não crítico):', metricsError);
          }
        }
      }

      // Remover do estado local APÓS confirmação do Firestore
      const completionIds = todayCompletions.map(c => c.id);
      setHabitCompletions(prev => {
        const filtered = prev.filter(comp => !completionIds.includes(comp.id));
        console.log('🟡 Atualizando estado local, removendo:', completionIds);
        return filtered;
      });

      // Atualizar progresso diário
      await updateDailyProgress();
      
      console.log('✅ uncompleteHabit finalizado com sucesso');
      
    } catch (error: any) {
      console.error('❌ Erro ao descompletar hábito:', error);
      throw error;
    }
  };

  const isHabitCompletedToday = (habitId: string): boolean => {
    const todayCompletions = getTodayCompletions();
    const isCompleted = todayCompletions.some(completion => completion.habitId === habitId);
    console.log('🔍 isHabitCompletedToday:', {
      habitId,
      todayCompletions: todayCompletions.length,
      isCompleted,
      completionsForThisHabit: todayCompletions.filter(c => c.habitId === habitId)
    });
    return isCompleted;
  };

  // Função para obter o completion de hoje para um hábito específico (incluindo customValue)
  const getTodayHabitCompletion = (habitId: string): HabitCompletion | null => {
    const todayCompletions = getTodayCompletions();
    const completion = todayCompletions.find(completion => completion.habitId === habitId);
    console.log('🔍 getTodayHabitCompletion:', {
      habitId,
      foundCompletion: !!completion,
      completion: completion ? {
        id: completion.id,
        habitId: completion.habitId,
        customValue: completion.customValue,
        notes: completion.notes,
        completedAt: completion.completedAt.toISOString()
      } : null
    });
    return completion || null;
  };

  const isStackCompletedToday = (stackId: string): boolean => {
    const todayCompletions = getTodayCompletions();
    return todayCompletions.some(completion => completion.habitStackId === stackId);
  };

  // MELHOR SEQUÊNCIA GERAL - Para dashboard (considera todos os hábitos)
  // Esta função foi movida para antes para resolver dependências

  const getCompletionRate = (habitId?: string): number => {
    if (habitId) {
      return isHabitCompletedToday(habitId) ? 100 : 0;
    }
    
    const totalItems = habits.length + habitStacks.length;
    const todayCompletions = getTodayCompletions();
    const completedItems = todayCompletions.length;
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  const getStreakData = (): StreakData | null => {
    return streakData;
  };

  const updateStreakData = async () => {
    // Implementação simplificada
    // Esta função será implementada posteriormente se necessário
  };

  const getTodayCompletionPercentage = (): number => {
    return getCompletionRate();
  };

  const recalculateStreaks = () => {
    // Implementação simplificada
    // Esta função será implementada posteriormente se necessário
  };

  const getHabitHistory = async (habitId: string, startDate: string, endDate: string) => {
    if (!currentUser) {
      return [];
    }

    try {
      // Para o histórico, fazer uma consulta específica apenas quando necessário
      const completionsQuery = query(
        collection(db, 'habitCompletions'),
        where('userId', '==', currentUser.uid),
        where('habitId', '==', habitId)
      );

      const snapshot = await getDocs(completionsQuery);
      const completionsByDate = new Map();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.completedAt) {
          try {
            const completionDate = format(data.completedAt.toDate(), 'yyyy-MM-dd');
            
            if (completionDate >= startDate && completionDate <= endDate) {
              completionsByDate.set(completionDate, {
                date: completionDate,
                completed: true,
                customValue: data.customValue,
                notes: data.notes
              });
            }
          } catch (dateError) {
            console.warn('Erro ao processar data de conclusão:', dateError);
          }
        }
      });

      // Criar array com todos os dias do período
      const history = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date().toISOString().split('T')[0];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = format(d, 'yyyy-MM-dd');
        
        // Se for hoje, incluir o estado atual do contexto com valores em memória
        if (dateStr === today) {
          const isTodayCompleted = isHabitCompletedToday(habitId);
          const todayCompletion = getTodayHabitCompletion(habitId);
          const existingToday = completionsByDate.get(dateStr);
          
          const todayData = {
            date: dateStr,
            completed: isTodayCompleted,
            // Priorizar valores em memória (todayCompletion) sobre valores do Firestore
            customValue: todayCompletion?.customValue ?? existingToday?.customValue,
            notes: todayCompletion?.notes ?? existingToday?.notes
          };
          
          console.log('🔍 getHabitHistory - Dados de hoje:', {
            habitId,
            dateStr,
            isTodayCompleted,
            todayCompletion: todayCompletion ? {
              customValue: todayCompletion.customValue,
              notes: todayCompletion.notes
            } : null,
            existingToday,
            finalTodayData: todayData
          });
          
          history.push(todayData);
        } else {
          // Para outros dias, usar dados do Firestore
          history.push(
            completionsByDate.get(dateStr) || {
              date: dateStr,
              completed: false
            }
          );
        }
      }

      return history;
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      return [];
    }
  };

  const addHistoryCompletion = async (habitId: string, date: string, data: {
    customValue?: string | number;
    notes?: string;
  }) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      console.log('🟡 Iniciando addHistoryCompletion para:', habitId, date);
      
      const completionDate = new Date(date + 'T12:00:00');
      
      // Verificar se já existe completion para esta data
      const startOfDate = startOfDay(completionDate);
      const endOfDate = endOfDay(completionDate);
      
      const existingCompletion = habitCompletions.find(completion => {
        if (completion.habitId !== habitId) return false;
        const compDate = completion.completedAt;
        return compDate >= startOfDate && compDate <= endOfDate;
      });

      if (existingCompletion) {
        console.log('⚠️ Já existe completion para esta data:', date);
        return; // Já existe completion para esta data
      }
      
      const completionData: any = {
        userId: currentUser.uid,
        habitId,
        completedAt: Timestamp.fromDate(completionDate)
      };

      // Adicionar campos opcionais apenas se não forem undefined
      if (data.notes !== undefined) {
        completionData.notes = data.notes;
      }
      if (data.customValue !== undefined) {
        completionData.customValue = data.customValue;
      }

      console.log('🟡 Salvando completion histórico no Firestore...', completionData);
      const docRef = await addDoc(collection(db, 'habitCompletions'), completionData);
      console.log('✅ Salvo no Firestore com ID:', docRef.id);
      
      // Adicionar ao estado local APÓS confirmação do Firestore
      const newCompletion = {
        id: docRef.id,
        ...completionData,
        completedAt: completionDate
      } as HabitCompletion;
      
      setHabitCompletions(prev => {
        console.log('🟡 Atualizando estado local com completion histórico:', newCompletion);
        return [...prev, newCompletion];
      });

      // Atualizar métricas se o valor é numérico
      if (data.customValue !== undefined && typeof data.customValue === 'number') {
        try {
          await HabitMetricsService.addCompletion(currentUser.uid, habitId, data.customValue, completionDate);
          console.log('📊 Métricas históricas atualizadas para hábito:', habitId);
        } catch (metricsError) {
          console.warn('⚠️ Erro ao atualizar métricas históricas (não crítico):', metricsError);
        }
      }
      
      // Atualizar progresso diário se for hoje
      const today = format(new Date(), 'yyyy-MM-dd');
      if (date === today) {
        await updateDailyProgress();
      }
      
      console.log('✅ addHistoryCompletion finalizado com sucesso');
      
    } catch (error: any) {
      console.error('❌ Erro ao adicionar conclusão histórica:', error);
      throw error;
    }
  };

  const removeHistoryCompletion = async (habitId: string, date: string) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      console.log('🟡 Iniciando removeHistoryCompletion para:', habitId, date);
      
      // 🔧 CORREÇÃO: Criar data explicitamente no timezone local
      const targetDate = new Date(date + 'T12:00:00'); // Meio-dia para evitar problemas de timezone
      const startOfDate = startOfDay(targetDate);
      const endOfDate = endOfDay(targetDate);
      
      console.log('🔍 DEBUG removeHistoryCompletion:', {
        inputDate: date,
        targetDate: targetDate.toISOString(),
        startOfDate: startOfDate.toISOString(),
        endOfDate: endOfDate.toISOString(),
        habitId,
        totalCompletions: habitCompletions.length
      });

      // Encontrar completions desta data no estado local
      const completionsToDelete = habitCompletions.filter(completion => {
        if (completion.habitId !== habitId) return false;
        const completionDate = completion.completedAt;
        const isInRange = completionDate >= startOfDate && completionDate <= endOfDate;
        
        console.log('🔍 Verificando completion:', {
          completionId: completion.id,
          completionDate: completionDate.toISOString(),
          completionDateFormatted: format(completionDate, 'yyyy-MM-dd'),
          isInRange,
          startOfDate: startOfDate.toISOString(),
          endOfDate: endOfDate.toISOString()
        });
        
        return isInRange;
      });

      if (completionsToDelete.length === 0) {
        console.log('⚠️ Nenhuma completion encontrada para deletar:', date);
        return; // Nenhuma completion para deletar
      }

      console.log('🟡 Deletando completions do Firestore:', completionsToDelete.map(c => c.id));
      
      // Deletar do Firestore
      const deletePromises = completionsToDelete.map(completion => 
        deleteDoc(doc(db, 'habitCompletions', completion.id))
      );
      await Promise.all(deletePromises);
      
      console.log('✅ Deletado do Firestore');

      // Atualizar métricas para completions que tinham valores numéricos
      const removalDate = new Date(date + 'T12:00:00');
      for (const completion of completionsToDelete) {
        if (completion.customValue !== undefined && typeof completion.customValue === 'number') {
          try {
            await HabitMetricsService.removeCompletion(currentUser.uid, habitId, completion.customValue, removalDate);
            console.log('📊 Métricas históricas atualizadas (remoção) para hábito:', habitId);
          } catch (metricsError) {
            console.warn('⚠️ Erro ao atualizar métricas históricas na remoção (não crítico):', metricsError);
          }
        }
      }

      // Remover do estado local APÓS confirmação do Firestore
      const completionIds = completionsToDelete.map(c => c.id);
      setHabitCompletions(prev => {
        const filtered = prev.filter(comp => !completionIds.includes(comp.id));
        console.log('🟡 Atualizando estado local, removendo:', completionIds);
        return filtered;
      });

      // Atualizar progresso diário se for hoje
      const today = format(new Date(), 'yyyy-MM-dd');
      if (date === today) {
        await updateDailyProgress();
      }
      
      console.log('✅ removeHistoryCompletion finalizado com sucesso');
      
    } catch (error: any) {
      console.error('❌ Erro ao remover conclusão histórica:', error);
      throw error;
    }
  };

  const refreshData = async () => {
    await loadAllData();
  };

  // === HABIT METRICS FUNCTIONS ===
  
  const initializeHabitMetrics = async (habitId: string) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      await HabitMetricsService.initializeMetrics(currentUser.uid, habitId);
      console.log('✅ Métricas inicializadas para hábito:', habitId);
    } catch (error: any) {
      console.error('❌ Erro ao inicializar métricas:', error);
      throw error;
    }
  };

  const getHabitMetrics = async (habitId: string) => {
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      const metrics = await HabitMetricsService.getMetricsDisplay(currentUser.uid, habitId);
      console.log('📊 Métricas obtidas para hábito:', habitId, metrics);
      return metrics;
    } catch (error: any) {
      console.error('❌ Erro ao obter métricas:', error);
      return null;
    }
  };

  return (
    <HabitsContext.Provider
      value={{
        habits,
        habitStacks,
        habitCompletions,
        todayProgress,
        loading,
        createHabit,
        updateHabit,
        deleteHabit,
        createHabitStack,
        updateHabitStack,
        deleteHabitStack,
        completeHabit,
        uncompleteHabit,
        isHabitCompletedToday,
        isStackCompletedToday,
        getTodayCompletions,
        getCurrentStreak,
        getLongestStreak,
        getBestCurrentStreak,
        getCompletionRate,
        getStreakData,
        updateStreakData,
        getTodayCompletionPercentage,
        recalculateStreaks,
        getHabitHistory,
        addHistoryCompletion,
        removeHistoryCompletion,
        refreshData,
        refreshHabitData,
        getHabitMetrics,
        initializeHabitMetrics
      }}
    >
      {children}
    </HabitsContext.Provider>
  );
};
